<template>
  <div :class="[classes.listCardWrapper, 'card']">
    <CardHeader :eventId="eventId" />

    <div :class="classes.eventMarketWrapper">
      <EventName
        :class="[classes.eventName, 'event-name']"
        :eventId="eventId"
      />

      <MarketName
        :marketId="market.marketId"
        :fallbackName="market.marketName"
        class="market-name"
      />
    </div>

    <div :style="listWrapperStyles" :class="[classes.listWrapper, layoutClass]">
      <ListOption
        v-for="(option, i) in options"
        :key="option.id"
        :stagger="i / 2"
        :option="option"
        :marketId="market.marketId"
      />
    </div>

    <div :class="classes.fade" />
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { computed, defineComponent } from "vue";
import {
  injectBrand,
  injectAdTag
} from "@fresh8/ad-data-composition/src/brand";

import ListOption from "./ListOption.vue";
import EventName from "../../EventName.vue";
import MarketName from "../../MarketName.vue";
import CardHeader from "../../CardHeader.vue";

import {
  composeMarket,
  composeListMarketOptions,
  composeEvent
} from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectCarouselContext } from "../../../lib/provideInjectors";

const useStyles = createUseStyles({
  listCardWrapper: {
    display: "flex",
    flex: "1 1 1px",
    flexDirection: "column",
    flexWrap: "wrap",
    height: "100%",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    background: props => props.cardBackgroundColor,
    borderRadius: props => `${props.borderRadius}px`,
    position: "relative",
    boxSizing: "border-box"
  },
  eventName: {
    marginBottom: "2px"
  },
  eventMarketWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "8px",
    boxSizing: "border-box"
  },
  listWrapper: {
    width: "100%",
    overflowX: "auto",
    boxSizing: "border-box",
    position: "relative",
    display: "grid",
    padding: "0 8px",
    gap: "8px",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gridAutoRows: "max(33px)",
    [`&.billboard`]: {
      maxHeight: "130px"
    },
    [`&.large-responsive"`]: {
      maxHeight: "132px"
    }
  },
  fade: {
    pointerEvents: "none",
    position: "absolute",
    height: "22px",
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: "0 0 4px 4px",
    background: props =>
      props.backgroundImage
        ? "none"
        : `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${props.cardBackgroundColor ||
            props.backgroundColor} 79.17%)`
  },
  spacer: {
    display: "flex",
    height: "3px"
  }
});

export default defineComponent({
  name: "LargeResponsive",
  components: {
    ListOption,
    CardHeader,
    EventName,
    MarketName
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const brandContext = injectBrand();
    const adTagContext = injectAdTag();
    const templateSizeContext = injectTemplateSize();
    const carouselContext = injectCarouselContext();

    const backgroundImage = computed(() => {
      return !!Object.keys(brandContext?.backgroundImageSets?.value || {})
        .length;
    });

    const relativeStyles = computed(() => ({
      ...(brandContext?.adTemplateBranding.value || {}),
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);
    const market = composeMarket(props.marketId);
    const marketOptions = composeListMarketOptions(props.marketId);
    const event = composeEvent(props.eventId);
    const fixtureStartDate = composeDate(event.value?.id);

    const listWrapperStyles = computed(() => {
      if (adTagContext?.responsive.value) {
        return { height: "133px" };
      } else {
        return { flex: "1" };
      }
    });

    return {
      listWrapperStyles,
      market,
      marketBranding: brandContext?.marketBranding,
      classes: classesRef,
      options: marketOptions,
      event,
      fixtureStartDate,
      layoutClass: computed(() => templateSizeContext?.size.value.name),

      marketNameStyle: {
        get(brand: Brand) {
          return brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand) => brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
